type PaginationProps = {
  page: number;
  totalCount: number;
  pageSize?: number;
  siblingCount?: number;
  onPageChange?: (page: number) => void;
  className?: string;
};

const DOTS = "...";

function range(start: number, end: number) {
  const length = end - start + 1;
  return Array.from({ length }, (_, index) => index + start);
}

function getPageItems({
  currentPage,
  totalPages,
  siblingCount,
}: {
  currentPage: number;
  totalPages: number;
  siblingCount: number;
}) {
  const totalPageNumbers = siblingCount + 5;

  if (totalPageNumbers >= totalPages) {
    return range(1, totalPages);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);
  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount = 3 + siblingCount * 2;
    return [...range(1, leftItemCount), DOTS, totalPages];
  }

  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount = 3 + siblingCount * 2;
    return [1, DOTS, ...range(totalPages - rightItemCount + 1, totalPages)];
  }

  return [
    1,
    DOTS,
    ...range(leftSiblingIndex, rightSiblingIndex),
    DOTS,
    totalPages,
  ];
}

export default function Pagination({
  page,
  totalCount,
  pageSize = 10,
  siblingCount = 1,
  onPageChange,
  className = "",
}: PaginationProps) {
  const totalPages = Math.ceil(totalCount / pageSize);

  if (totalPages <= 1) {
    return null;
  }

  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const pageItems = getPageItems({
    currentPage,
    totalPages,
    siblingCount,
  });

  const canMovePrev = currentPage > 1;
  const canMoveNext = currentPage < totalPages;

  const moveToPage = (nextPage: number) => {
    if (!onPageChange || nextPage === currentPage || nextPage < 1 || nextPage > totalPages) {
      return;
    }

    onPageChange(nextPage);
  };

  return (
    <nav
      className={`flex items-center justify-center gap-1 ${className}`}
      aria-label="페이지네이션"
    >
      <button
        type="button"
        onClick={() => moveToPage(currentPage - 1)}
        disabled={!canMovePrev}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        style={{ color: "#6B7280" }}
        aria-label="이전 페이지"
      >
        <i className="ri-arrow-left-s-line text-base"></i>
      </button>

      {pageItems.map((item, index) => {
        if (typeof item !== "number") {
          return (
            <span
              key={`${item}-${index}`}
              className="w-8 h-8 flex items-center justify-center text-xs"
              style={{ color: "#9CA3AF" }}
            >
              {item}
            </span>
          );
        }

        const isActive = item === currentPage;

        return (
          <button
            key={item}
            type="button"
            onClick={() => moveToPage(item)}
            disabled={isActive}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold tabular-nums transition-colors disabled:cursor-default"
            style={
              isActive
                ? { backgroundColor: "#2563EB", color: "#FFFFFF" }
                : { color: "#6B7280" }
            }
            aria-current={isActive ? "page" : undefined}
          >
            {item}
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => moveToPage(currentPage + 1)}
        disabled={!canMoveNext}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        style={{ color: "#6B7280" }}
        aria-label="다음 페이지"
      >
        <i className="ri-arrow-right-s-line text-base"></i>
      </button>
    </nav>
  );
}
